import type { paths } from '../../types/clients/payments'
import { computed, useApi, useCoreConfig, useOpenapi, useQuery, useUserInfo } from '#imports'
import type { UseOpenapiOptions, UseQueryOverrideOptions } from '../../types'

/**
 * @title usePaymentsClient
 * @see Docs https://payments-prod.spilnu.dk/api/documentation
 */
export const usePaymentsClient = (options: Pick<UseOpenapiOptions, 'ignoreUnauthorized' | 'reportErrors'> = {}) => {
    const { site: { brand } } = useCoreConfig()
    const client = useOpenapi<paths>({
        // The payments spec moved off l5-swagger to dedoc/scramble, which drops the `/api`
        // prefix from path keys (`/public/...`), so it lives on the baseUrl instead.
        baseUrl: '/gateway/payments/api',
        service: 'payments',
        ...options,
    })

    function useGetDepositSuccessfulCount(options: UseQueryOverrideOptions = {}) {
        // TODO: Fix when openapi spec works again in payments service, so we get a properly typed client
        const api = useApi({
            baseURL: '/gateway/payments',
            service: 'payments'
        })
        const { isLoggedIn } = useUserInfo()

        const query = useQuery(({ signal }) => {
            return api<{
                /**
                 * Number of successful deposists a user has made.
                 * Zero equals no deposits made.
                 */
                count: number
            }>('/api/public/cashier/deposit/successful-count', {
                signal,
                query: {
                    brand
                }
            })
        }, {
            server: false,
            lazy: true,
            ...options,
            key: 'payments:cashier:deposit:successful-count',
            enabled: isLoggedIn
        })

        const isFirstDeposit = computed(() => {
            return query.data.value?.count === 0
        })

        return query.enrich({
            isFirstDeposit
        })
    }

    return {
        client,
        useGetDepositSuccessfulCount
    }
}
